<script setup lang="ts">
import { CheckboxComponent, PaginationComponent } from '@metapic-apps/shared'
import type { CheckboxValueType } from 'element-plus'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useGetCampaigns } from '@/api'
import { useGetNotificationStatuses } from '@/api/notifications.api'
import AddPlusIcon from '@/assets/icons/add-plus.svg?component'
import ChevronIcon from '@/assets/icons/chevron.svg?component'
import AdvertiserDetailsCampaignsCreatorFilter from '@/components/Advertiser/AdvertiserDetailsCampaignsCreatorFilter.vue'
import CampaignDeletePopup from '@/components/CampaignDeletePopup/CampaignDeletePopup.vue'
import CampaignListItem from '@/components/CampaignListItem/CampaignListItem.vue'
import CampaignOverview from '@/components/CampaignOverview/CampaignOverview.vue'
import EmptyState from '@/components/EmptyState/EmptyState.vue'
import { type CampaignsFilterType, CampaignStatusEnum } from '@/lib/types/campaigns.types'
import { BoxComponent, ButtonComponent, FlexComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{ advertiserGroupId?: number; advertiserId: number; advertiserName: string }>()
const router = useRouter()
const route = useRoute()
const isCampaignOverviewOpen = ref(!!route.query.campaignId)
const deleteCampaignId = ref<number | undefined>()
const isDeletePopupOpen = ref(false)
const filter = ref<CampaignsFilterType>({
  advertiser: { id: props.advertiserId, label: props.advertiserName },
  statuses: [CampaignStatusEnum.Active, CampaignStatusEnum.Scheduled],
  include: ['targets'],
  size: 6,
})
const statuses = [
  { value: CampaignStatusEnum.Active, label: 'Active' },
  { value: CampaignStatusEnum.Scheduled, label: 'Scheduled' },
  { value: CampaignStatusEnum.Paused, label: 'Paused' },
  { value: CampaignStatusEnum.Ended, label: 'Ended' },
]
const activeCheckboxFilters = computed(() => {
  return filter.value?.statuses?.length || 0
})
const userSearchQuery = ref<string>('')
const { data, isLoading, totalPages } = useGetCampaigns(filter)
const campaignIds = computed(() => data.value?.map((campaign) => campaign.id) || [])
const { notificationStatuses } = useGetNotificationStatuses(campaignIds)
const isFilterable = ref(false)
const handleStatus = (value: CheckboxValueType[]) => {
  filter.value = { ...filter.value, statuses: value as CampaignStatusEnum[] }
}
const refreshList = () => {
  filter.value = { ...filter.value }
}
const openCampaignOverview = async (campaignId: number) => {
  isCampaignOverviewOpen.value = true
  await router.push({ query: { ...route.query, campaignId } })
}
const openDeleteCampaignPopup = (id: number) => {
  deleteCampaignId.value = id
  isDeletePopupOpen.value = true
}
const onDeleteCampaign = async () => {
  deleteCampaignId.value = undefined
  isDeletePopupOpen.value = false
  isCampaignOverviewOpen.value = false
  await router.push({ query: { ...route.query, campaignId: undefined } })
  refreshList()
}
const onTargetUserId = (targetUserId?: number) => {
  filter.value = { ...filter.value, targetUserId }
}

watch(
  data,
  (campaigns) => {
    if (campaigns?.length) {
      isFilterable.value = true
    }
  },
  { immediate: true },
)
watch(
  () => route.query.campaignId,
  (campaignId) => {
    isCampaignOverviewOpen.value = Boolean(campaignId)
  },
)
</script>
<template>
  <BoxComponent v-if="isFilterable" :mb="24">
    <FlexComponent align="center" justify="space-between" :gap="16" wrap="wrap">
      <FlexComponent align="center" justify="space-between" :gap="16" wrap="wrap">
        <AdvertiserDetailsCampaignsCreatorFilter
          v-if="props.advertiserGroupId"
          :advertiser-group-id="props.advertiserGroupId"
          @creator-change="onTargetUserId"
        />
        <el-dropdown trigger="click" :hide-on-click="false">
          <BoxComponent :p="16">
            <FlexComponent :gap="8" align="center">
              <TypographyComponent variant="label-large"> Status </TypographyComponent>
              <TypographyComponent
                v-if="activeCheckboxFilters > 0"
                variant="label-medium"
                color="var(--inverted)"
                class="filter--filter-amount"
              >
                {{ activeCheckboxFilters }}
              </TypographyComponent>
              <el-icon :size="24" class="chevron-icon">
                <ChevronIcon />
              </el-icon>
            </FlexComponent>
          </BoxComponent>
          <template #dropdown>
            <BoxComponent :pt="8" :pb="8">
              <FlexComponent direction="column">
                <el-checkbox-group :model-value="filter.statuses" @change="(val: CheckboxValueType[]) => handleStatus(val)">
                  <FlexComponent direction="column">
                    <CheckboxComponent v-for="item in statuses" :key="item.value" :value="item.value" class="filter--checkbox-filter">
                      {{ item.label }}
                    </CheckboxComponent>
                  </FlexComponent>
                </el-checkbox-group>
              </FlexComponent>
            </BoxComponent>
          </template>
        </el-dropdown>
      </FlexComponent>
      <ButtonComponent variant="primary" to="/campaigns/create">
        <template #iconLeft>
          <el-icon :size="24" color="var(--inverted)">
            <AddPlusIcon />
          </el-icon>
        </template>
        Create campaign
      </ButtonComponent>
    </FlexComponent>
  </BoxComponent>
  <LoadingComponent v-if="isLoading" :size="64" />
  <template v-else-if="data?.length">
    <BoxComponent v-for="campaign in data" :key="campaign.id" :mb="16">
      <CampaignListItem
        :item="campaign"
        :is-compact="true"
        :show-targets="true"
        :has-update="notificationStatuses.has(campaign.id)"
        @on-open="openCampaignOverview"
      />
    </BoxComponent>
    <BoxComponent v-if="totalPages !== 0 && data && data?.length > 0" :pt="32" :pb="32">
      <PaginationComponent v-model:current-page="filter.page" :total-pages="totalPages" />
    </BoxComponent>
  </template>
  <EmptyState
    v-else
    :is-search-state="!!userSearchQuery"
    search-state-msg="No campaigns match your search criteria."
    empty-state-msg="No campaigns yet."
  >
    <template #cta>
      <ButtonComponent variant="primary" to="/campaigns/create"> Create campaign </ButtonComponent>
    </template>
  </EmptyState>
  <CampaignOverview v-model="isCampaignOverviewOpen" @on-refresh="refreshList" @on-delete="openDeleteCampaignPopup" />
  <CampaignDeletePopup v-model="isDeletePopupOpen" :campaign-id="deleteCampaignId" @on-delete="onDeleteCampaign" />
</template>
<style lang="scss" scoped>
:deep(.el-icon) {
  path {
    fill: currentColor;
  }
}

:deep(.el-tooltip__trigger) {
  cursor: pointer;
  &:hover,
  &[aria-expanded='true'] {
    border-radius: 8px;
    background: var(--button-hover);
  }
  i {
    transition: all 0.3s ease-in;
  }

  &[aria-expanded='true'] {
    .chevron-icon {
      transform: rotate(180deg);
    }
  }
}

.filter--filter-amount {
  border-radius: 24px;
  background: var(--primary);
  width: 20px;
  height: 20px;
  text-align: center;
  line-height: 20px;
}
.filter--checkbox-filter {
  padding: 16px;
  --el-checkbox-height: 56px;
}
</style>
